const CUSTOM_HS_MAX_LINE = 12;

/**
 * @param {string} line
 * @returns {string}
 */
export function sanitizeCustomHeatShrinkLine(line) {
  if (line == null || typeof line !== 'string') return '';
  return line.slice(0, CUSTOM_HS_MAX_LINE);
}

/** Custom printed artwork applies to "custom" or "clear+custom" (clear tube + custom print). */
export function isCustomPrintedHeatShrinkEnd(v) {
  return v === 'custom' || v === 'clear+custom';
}

/**
 * True if any per-end heat shrink value needs custom printed artwork
 * @param {object} finishing
 * @returns {boolean}
 */
export function hasAnyCustomHeatShrinkEnd(finishing) {
  const hs = finishing?.heatShrink;
  if (!hs) return false;
  if (typeof hs === 'string') return isCustomPrintedHeatShrinkEnd(hs);
  if (typeof hs === 'object' && hs.type) return isCustomPrintedHeatShrinkEnd(hs.type);
  return Object.values(hs).some((v) => isCustomPrintedHeatShrinkEnd(v));
}

/**
 * Shared artwork is complete for add-to-cart when:
 * - mode logo: public URL + grayscale attestation
 * - mode text: at least one non-empty line (each ≤12 chars)
 * @param {object} finishing
 * @returns {boolean}
 */
export function isCustomHeatShrinkArtworkComplete(finishing) {
  if (!hasAnyCustomHeatShrinkEnd(finishing)) return true;
  const ch = finishing.customHeatShrink;
  if (!ch || !ch.mode) return false;
  if (ch.mode === 'logo') {
    return !!(ch.logo?.publicUrl || ch.logo?.s3Key) && ch.attestedGrayscale === true;
  }
  if (ch.mode === 'text') {
    const a = (ch.line1 || '').trim();
    const b = (ch.line2 || '').trim();
    return a.length > 0 || b.length > 0;
  }
  return false;
}

/**
 * Count ends with custom heat shrink for pricing display
 * @param {object} finishing
 * @param {object} config cable config (for connector keys)
 * @returns {number}
 */
export function countCustomHeatShrinkEnds(finishing, config) {
  if (!finishing?.heatShrink || typeof finishing.heatShrink !== 'object') return 0;
  const hs = finishing.heatShrink;
  const keys = [];
  const isHybrid = config?.style && ['hybrid', 'hybrid-power-audio', 'hybrid-power-audio-audio', 'zip', 'zip-audio-audio', 'wye'].includes(config.style);
  if (!isHybrid) {
    if (config?.connectors?.endA) keys.push('endA');
    if (config?.connectors?.endB) keys.push('endB');
  }
  if (config?.hybridConnectors?.connectors) {
    Object.keys(config.hybridConnectors.connectors).forEach((k) => {
      if (config.hybridConnectors.connectors[k]) keys.push(k);
    });
  }
  if (config?.wyeConnectors?.connectors) {
    Object.keys(config.wyeConnectors.connectors).forEach((k) => {
      if (config.wyeConnectors.connectors[k]) keys.push(k);
    });
  }
  if (config?.zipConnectors?.connectors) {
    Object.keys(config.zipConnectors.connectors).forEach((k) => {
      if (config.zipConnectors.connectors[k]) keys.push(k);
    });
  }
  let n = 0;
  keys.forEach((key) => {
    const v = hs[key] !== undefined && hs[key] !== null ? hs[key] : hs.global;
    if (isCustomPrintedHeatShrinkEnd(v)) n += 1;
  });
  return n;
}

/**
 * One-line Finishing summary: per-end heat shrink + custom text or logo link (pipe-separated).
 * @param {object} finishing
 * @returns {string}
 */
export function buildHeatShrinkFinishingLine(finishing) {
  if (!finishing?.heatShrink) return '';

  let base = '';
  if (typeof finishing.heatShrink === 'string') {
    base = `Heat Shrink: ${finishing.heatShrink}`;
  } else if (typeof finishing.heatShrink === 'object') {
    const heatShrinkEntries = Object.entries(finishing.heatShrink)
      .filter(([key, value]) => key !== 'global' && value && value !== 'none')
      .map(([key, value]) => `${key}: ${value}`);

    if (heatShrinkEntries.length > 0) {
      base = `Heat Shrink: ${heatShrinkEntries.join(', ')}`;
    } else if (finishing.heatShrink.global && finishing.heatShrink.global !== 'none') {
      base = `Heat Shrink: ${finishing.heatShrink.global}`;
    } else {
      base = 'Heat Shrink: none';
    }
  }

  if (!hasAnyCustomHeatShrinkEnd(finishing) || !finishing.customHeatShrink) {
    return base;
  }

  const ch = finishing.customHeatShrink;
  const parts = [base];

  const tube = ch.tubeColor || 'black';
  const textCol = ch.ribbonColor || 'white';
  parts.push(`Heat-shrink tube: ${tube}, text color: ${textCol}`);

  if (ch.mode === 'text') {
    const l1 = (ch.line1 || '').trim();
    const l2 = (ch.line2 || '').trim();
    if (l1) parts.push(`Line 1: ${l1}`);
    if (l2) parts.push(`Line 2: ${l2}`);
  } else if (ch.mode === 'logo' && ch.logo?.publicUrl) {
    const fname = ch.logo?.s3Key ? ch.logo.s3Key.split('/').pop() : 'logo';
    parts.push(`Logo link: ${ch.logo.publicUrl} (${fname})`);
  }

  return parts.join(' | ');
}
